import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';

/**
 * MultiSelectDropdown — searchable multi-select dropdown
 * Props:
 *   label        string
 *   options      [{ value, label }]
 *   value        string  (comma-separated selected labels, stored as text)
 *   onChange     fn(string) — called with comma-separated labels
 *   placeholder  string
 *   loading      bool
 */
export default function MultiSelectDropdown({
  label,
  options = [],
  value = '',
  onChange,
  placeholder = 'Select options...',
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchRef = useRef(null);

  // Parse current value (comma-separated string) into array
  const selected = value
    ? value?.split(',')?.map(v => v?.trim())?.filter(Boolean)
    : [];

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (!containerRef?.current?.contains(e?.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focus search when opened
  useEffect(() => {
    if (open && searchRef?.current) {
      searchRef?.current?.focus();
    }
  }, [open]);

  const filtered = options?.filter(opt =>
    opt?.label?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const toggle = (optLabel) => {
    const next = selected?.includes(optLabel)
      ? selected?.filter(s => s !== optLabel)
      : [...selected, optLabel];
    onChange?.(next?.join(', '));
  };

  const removeTag = (e, tag) => {
    e?.stopPropagation();
    const next = selected?.filter(s => s !== tag);
    onChange?.(next?.join(', '));
  };

  const highlightMatch = (text) => {
    if (!search) return text;
    const idx = text?.toLowerCase()?.indexOf(search?.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text?.slice(0, idx)}
        <mark className="bg-teal-100 text-teal-800 rounded px-1">{text?.slice(idx, idx + search?.length)}</mark>
        {text?.slice(idx + search?.length)}
      </>
    );
  };

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-sm font-medium text-slate-700 mb-6">{label}</label>
      )}

      {/* Trigger */}
      <div
        onClick={() => setOpen(prev => !prev)}
        className={`min-h-[40px] w-full border rounded-lg px-10 py-7 flex flex-wrap items-center gap-6 cursor-pointer bg-white transition-colors ${
          open
            ? 'border-primary ring-2 ring-primary/20' :'border-slate-200 hover:border-slate-300'
        }`}
      >
        {loading ? (
          <span className="flex items-center gap-6 text-sm text-slate-400">
            <Icon name="Loader2" size={14} className="animate-spin" />
            Loading...
          </span>
        ) : selected?.length === 0 ? (
          <span className="text-sm text-slate-400">{placeholder}</span>
        ) : (
          selected?.map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-4 px-8 py-3 bg-teal-50 text-teal-700 border border-teal-200 rounded-md text-xs font-medium"
            >
              {tag}
              <button
                type="button"
                onClick={(e) => removeTag(e, tag)}
                className="hover:text-teal-900 transition-colors"
              >
                <Icon name="X" size={10} />
              </button>
            </span>
          ))
        )}
        <Icon
          name="ChevronDown"
          size={14}
          className={`ml-auto text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-4 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          {/* Search */}
          <div className="p-8 border-b border-slate-100">
            <div className="relative">
              <Icon name="Search" size={13} className="absolute left-10 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                placeholder="Search..."
                className="w-full pl-28 pr-10 py-7 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-8 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <Icon name="X" size={12} />
                </button>
              )}
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-[220px] overflow-y-auto">
            {filtered?.length === 0 ? (
              <div className="px-14 py-12 text-sm text-slate-400 text-center">No options found</div>
            ) : (
              filtered?.map(opt => {
                const isSelected = selected?.includes(opt?.label);
                return (
                  <button
                    key={opt?.value}
                    type="button"
                    onClick={() => toggle(opt?.label)}
                    className={`w-full flex items-center justify-between px-14 py-10 text-sm text-left transition-colors ${
                      isSelected
                        ? 'bg-teal-50 text-teal-800' :'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <span>{highlightMatch(opt?.label)}</span>
                    {isSelected && <Icon name="Check" size={14} className="text-teal-600 flex-shrink-0" />}
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {selected?.length > 0 && (
            <div className="px-14 py-8 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-500">{selected?.length} selected</span>
              <button
                type="button"
                onClick={() => onChange?.('')}
                className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
